import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  setIsUpgradedToSeo,
  setIsLocalSeoExtResultAccessed
} from '../redux/localSeo';
import { setIsAiAddonPurchased } from '../redux/dashboard';
import useOrbitAIIntercom from './useOrbitAIIntercom';

interface UseIntercomUserDataProps {
  userDetails: any;
  isLocalSEOAddonBuy: any;
  isUpgradedToSeo: boolean;
  isAiAddonPurchased: boolean;
  isAiVisibilityVisit: boolean;
  isAiScoreView: boolean;
  isLocalSeoSqSp: boolean;
  isLocalSeoExtResultAccessed: boolean;
}

export const useIntercomUserData = ({
  userDetails,
  isLocalSEOAddonBuy,
  isUpgradedToSeo,
  isAiAddonPurchased,
  isAiVisibilityVisit,
  isAiScoreView,
  isLocalSeoSqSp,
  isLocalSeoExtResultAccessed
}: UseIntercomUserDataProps) => {
  const dispatch = useDispatch();
  const [userData, setUserData] = useState<any>({});
  const hasAccessedRef = useRef(false);

  const orbitData = useOrbitAIIntercom({ userDetails })

  // Memoize the base data builder to prevent unnecessary re-creations
  const getBaseData = useCallback(() => {
    const isLocalSeo = userDetails?.isLocalSeo || false;
    return {
      name: userDetails?.name,
      email: userDetails?.email,
      is_plugin_installed: !!document.querySelector('#rough-water-seo-root'),
      user_hash: userDetails?.hash,
      localextension: isLocalSeo === true || isLocalSeo === 'true' ? 'yes' : 'no',
      orbit_purchased: orbitData?.orbit_purchased,
      orbit_used: orbitData?.orbit_used
    };
  }, [userDetails, orbitData?.orbit_purchased, orbitData?.orbit_used]);

  useEffect(() => {
    if (orbitData) {
      setUserData((prev: any) => ({
        ...getBaseData(),
        ...prev,
        orbit_purchased: orbitData.orbit_purchased,
        orbit_used: orbitData.orbit_used
      }));
    }
  }, [orbitData?.orbit_purchased, orbitData?.orbit_used, getBaseData]);

  useEffect(() => {
    if (isLocalSEOAddonBuy) {
      setUserData({
        ...getBaseData(),
        local_seo_addon: isLocalSEOAddonBuy
      });
    }
  }, [isLocalSEOAddonBuy, getBaseData]);

  useEffect(() => {
    if (isUpgradedToSeo) {
      dispatch(setIsUpgradedToSeo(false));
      setUserData({
        ...getBaseData(),
        upgradefromlocalextension: 'yes'
      });
    }
  }, [isUpgradedToSeo, dispatch, getBaseData]);

  useEffect(() => {
    if (isAiAddonPurchased) {
      dispatch(setIsAiAddonPurchased(false));
      setUserData({
        ...getBaseData(),
        ai_addon_purchase: 'yes'
      });
    }
  }, [isAiAddonPurchased, dispatch, getBaseData]);

  useEffect(() => {
    if (isAiVisibilityVisit) {
      setUserData({
        ...getBaseData(),
        ai_visibility_visit: 'yes'
      });
    }
  }, [isAiVisibilityVisit, getBaseData]);

  useEffect(() => {
    if (isAiScoreView) {
      setUserData({
        ...getBaseData(),
        ai_score_view: 'yes'
      });
    }
  }, [isAiScoreView, getBaseData]);

  useEffect(() => {
    if (isLocalSeoSqSp) {
      setUserData({
        ...getBaseData(),
        localext_dropped: userDetails?.temp ? 'yes' : 'no'
      });
    }
  }, [userDetails, isLocalSeoSqSp, getBaseData]);

  useEffect(() => {
    try {
      if (isLocalSeoExtResultAccessed && !hasAccessedRef.current) {
        hasAccessedRef.current = true;
        setUserData({
          ...getBaseData(),
          results_accessed: 'yes',
          localextension: 'yes'
        });
        dispatch(setIsLocalSeoExtResultAccessed(false));
      }
    } catch (error) {
      // Intentionally suppressed error for result access sequence
    }
  }, [isLocalSeoExtResultAccessed, dispatch, getBaseData]);

  return {
    userData: {
      ...getBaseData(),
      ...userData,
      orbit_purchased: orbitData?.orbit_purchased,
      orbit_used: orbitData?.orbit_used
    },
    setUserData
  };
};
